<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-40">
    <!-- Connection Warning -->
    <div v-if="!isConnected || heartbeatError" class="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div>
        <p class="text-sm font-medium text-yellow-800">Verbindungsproblem</p>
        <p class="text-xs text-yellow-600">{{ heartbeatError || 'Verbindung wird wiederhergestellt...' }}</p>
      </div>
    </div>

    <!-- Lock Warning -->
    <div v-if="lockInfo.isLocked" class="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
      <div class="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <div>
          <p class="text-sm font-medium text-red-800">Check-In ist gesperrt</p>
          <p class="text-xs text-red-600">Bearbeitet von: {{ lockInfo.lockedBy }}</p>
        </div>
      </div>
      <button
        v-if="lockInfo.canResume"
        @click="router.push('/checks/start')"
        class="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
      >
        Neuen Check starten
      </button>
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-4 bg-white rounded-xl p-4 shadow-sm">
      <div>
        <h1 class="text-xl font-bold text-gray-800">Services auswählen</h1>
        <p class="text-sm text-gray-500">Wähle die benötigten Services aus</p>
      </div>
      <div class="text-3xl">📋</div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="bg-white rounded-xl p-8 flex flex-col items-center justify-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mb-4"></div>
      <p class="text-gray-600">Lade Services...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-white rounded-xl p-8 flex flex-col items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p class="text-gray-600 mb-4">{{ error }}</p>
      <button
        @click="router.push('/checks/start')"
        class="px-4 py-2 bg-[#E9041D] text-white rounded-lg hover:bg-red-700"
      >
        Zurück zum Start
      </button>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-4">
      <!-- Progress Indicator (for mandatory groups) -->
      <div v-if="hasMandatoryGroups" class="bg-white rounded-xl p-4 shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">Pflichtfelder</span>
          <span class="text-sm font-bold" :class="progressInfo.percentage === 100 ? 'text-green-600' : 'text-gray-600'">
            {{ progressInfo.completed }} von {{ progressInfo.total }}
          </span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2.5">
          <div
            class="h-2.5 rounded-full transition-all duration-300"
            :class="progressInfo.percentage === 100 ? 'bg-green-500' : 'bg-[#E9041D]'"
            :style="{ width: progressInfo.percentage + '%' }"
          ></div>
        </div>
      </div>

      <!-- Recently Used Services -->
      <div v-if="recentlyUsedServices.length > 0 && !isAddingServices" class="bg-white rounded-xl p-4 shadow-sm">
        <p class="text-sm font-medium text-gray-700 mb-3">⏱ Zuletzt verwendet</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="service in recentlyUsedServices.slice(0, 6)"
            :key="service.id"
            @click="toggleRecentService(service.id)"
            class="px-3 py-1.5 text-sm rounded-full border-2 transition-all duration-150"
            :class="selectedServices.includes(service.id)
              ? 'border-[#E9041D] bg-red-50 text-[#E9041D]'
              : isServiceExcluded(service.id)
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed opacity-50'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'"
          >
            {{ service.name }}
          </button>
        </div>
      </div>

      <!-- Existing Services — read-only status overview, shown in BOTH modes so a
           re-entered initial selection is no longer blind to booked services (DEV-167).
           Removal happens by tapping the booked service in the selection area below. DEV-119 -->
      <div v-if="existingServices.length > 0" class="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h2 class="text-lg font-medium text-blue-800 mb-1">Bereits zugewiesene Services</h2>
        <p class="text-xs text-blue-600 mb-3">Zum Entfernen den Service unten im Auswahlbereich antippen.</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="service in existingServices"
            :key="service.id"
            class="px-3 py-1.5 text-sm rounded-full flex items-center gap-1.5"
            :class="getExistingSummaryClasses(service)"
          >
            <span :class="{ 'line-through': servicesToRemove.includes(service.id) }">{{ service.name }}</span>
            <svg
              v-if="servicesToRemove.includes(service.id)"
              class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <svg
              v-else-if="isServiceCompleted(service)"
              class="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <svg
              v-else-if="isServiceStarted(service)"
              class="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </span>
        </div>
        <p v-if="servicesToRemove.length > 0" class="text-xs text-red-600 mt-2">
          {{ servicesToRemove.length }} Service(s) zum Entfernen markiert – mit „Speichern" bestätigen.
        </p>
      </div>

      <!-- Service groups failed to load — fail closed instead of silently rendering
           an unvalidated flat list; saving is blocked until the retry succeeds. DEV-167 -->
      <div v-if="serviceGroupsError" class="bg-red-50 rounded-xl p-4 border border-red-200">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div class="flex-1">
            <p class="text-red-700 text-sm font-medium">Service-Gruppen konnten nicht geladen werden.</p>
            <p class="text-red-600 text-xs mt-0.5">Ohne Gruppenprüfung ist das Speichern gesperrt.</p>
            <button
              @click="fetchServiceGroups(vehicleRentalCompanyId)"
              class="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Erneut laden
            </button>
          </div>
        </div>
      </div>

      <!--
        DEV-240 / TRK-15: zwei Sätze, zwei Bedingungen. Der Turnaround entfällt
        bei einer Abholung; die automatische Schlüsselübergabe hängt dagegen an
        `hands_over_custody` und trägt auf der Produktion ein ANDERER Service.
        Beides aus einer Bedingung zu versprechen hiesse, eine Übergabe
        anzukündigen, die ausbleibt — der Schlüssel bliebe „bereit" (DEV-226).
      -->
      <div v-if="turnaroundIsReplaced || keyWillBeHandedOver" class="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
        <span v-if="turnaroundIsReplaced">
          Abholung durch Fremdfirma gewählt — der Turnaround ist für diesen Check-In nicht erforderlich.
        </span>
        <span v-if="keyWillBeHandedOver">
          Nach Abschluss wird der Schlüssel automatisch übergeben.
        </span>
      </div>

      <!-- Tab Navigation for Service Groups -->
      <div v-if="serviceGroups.length > 0" class="bg-white rounded-xl shadow-sm overflow-hidden">
        <!-- Tabs -->
        <div class="border-b border-gray-200">
          <div class="flex overflow-x-auto scrollbar-hide">
            <button
              v-for="(group, index) in serviceGroups"
              :key="group.id"
              @click="activeTab = index"
              class="flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
              :class="activeTab === index
                ? 'border-[#E9041D] text-[#E9041D] bg-red-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            >
              <span class="mr-1.5">{{ getIconForGroup(group.name) }}</span>
              {{ group.name }}
              <span
                v-if="isGroupValid(group)"
                class="ml-1.5 text-green-500"
              >✓</span>
              <span
                v-else-if="isGroupMandatory(group)"
                class="ml-1.5 w-2 h-2 bg-red-500 rounded-full inline-block"
              ></span>
            </button>
            <!-- Weitere Tab -->
            <button
              v-if="displayableServices.length > 0"
              @click="activeTab = serviceGroups.length"
              class="flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
              :class="activeTab === serviceGroups.length
                ? 'border-[#E9041D] text-[#E9041D] bg-red-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            >
              📋 Weitere
            </button>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="p-4">
          <!-- Service Group Content -->
          <div v-if="activeTab < serviceGroups.length">
            <ServiceGroupCard
              :group="serviceGroups[activeTab]"
              :selected-services="selectedServices"
              :existing-services="effectiveExistingServiceIds"
              :locked-existing-services="lockedExistingServiceIds"
              :removable-existing-services="removableExistingServiceIds"
              :removing-services="servicesToRemove"
              :exclusion-conflicts="exclusionConflicts"
              :validation-errors="validationErrors"
              :turnaround-replaced="turnaroundIsReplaced"
              :default-expanded="true"
              @update:selected-services="handleGroupSelection"
              @request-remove-existing="handleRequestRemoveExisting"
              @swap-blocked="handleSwapBlocked"
              @exclusion-blocked="handleExclusionBlocked"
              @toggle-remove-existing="handleToggleRemoveExisting"
            />
          </div>

          <!-- Weitere Services Content -->
          <div v-else-if="displayableServices.length > 0">
            <p class="text-sm text-gray-500 mb-4">Weitere verfügbare Services (optional)</p>
            <div class="space-y-2">
              <label
                v-for="service in displayableServices"
                :key="service.id"
                class="flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all"
                :class="getServiceClasses(service)"
                @click="onAvailableTileClick(service)"
              >
                <div
                  class="w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0 transition-colors"
                  :class="getServiceIndicatorClasses(service)"
                >
                  <svg
                    v-if="servicesToRemove.includes(service.id)"
                    class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <svg
                    v-else-if="selectedServices.includes(service.id) || service.isAlreadyAssigned"
                    class="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div class="flex-1">
                  <span class="font-medium text-gray-900 text-sm" :class="{ 'line-through': servicesToRemove.includes(service.id) }">{{ service.name }}</span>
                  <span v-if="servicesToRemove.includes(service.id)" class="text-xs text-red-600 ml-2">(wird entfernt)</span>
                  <span v-else-if="service.isAlreadyAssigned && canRemoveExistingById(service.id)" class="text-xs text-green-600 ml-2">(zugewiesen – antippen zum Entfernen)</span>
                  <span v-else-if="service.isAlreadyAssigned" class="text-xs text-green-600 ml-2">(Bereits zugewiesen)</span>
                  <span v-else-if="isServiceExcluded(service.id)" class="text-xs text-gray-400 ml-2">(nicht kombinierbar)</span>
                  <p v-if="service.description" class="text-xs text-gray-500 mt-0.5">{{ service.description }}</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Flat List (no groups) -->
      <div v-else class="bg-white rounded-xl shadow-sm p-4">
        <p class="text-sm text-gray-500 mb-4">Verfügbare Services</p>
        <div class="space-y-2">
          <label
            v-for="service in availableServices"
            :key="service.id"
            class="flex items-center p-3 rounded-lg border-2 transition-all"
            :class="getServiceClasses(service)"
            @click="onAvailableTileClick(service)"
          >
            <div
              class="w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0"
              :class="getServiceIndicatorClasses(service)"
            >
              <svg
                v-if="servicesToRemove.includes(service.id)"
                class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <svg
                v-else-if="selectedServices.includes(service.id) || service.isAlreadyAssigned"
                class="w-3 h-3 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="flex-1">
              <span class="font-medium text-gray-900 text-sm" :class="{ 'line-through': servicesToRemove.includes(service.id) }">{{ service.name }}</span>
              <span v-if="servicesToRemove.includes(service.id)" class="text-xs text-red-600 ml-2">(wird entfernt)</span>
              <span v-else-if="service.isAlreadyAssigned && canRemoveExistingById(service.id)" class="text-xs text-green-600 ml-2">(zugewiesen – antippen zum Entfernen)</span>
              <span v-else-if="service.isAlreadyAssigned" class="text-xs text-green-600 ml-2">(Bereits zugewiesen)</span>
              <span v-else-if="isServiceExcluded(service.id)" class="text-xs text-gray-400 ml-2">(nicht kombinierbar)</span>
              <p v-if="service.description" class="text-xs text-gray-500 mt-0.5">{{ service.description }}</p>
            </div>
          </label>
        </div>
      </div>

      <!-- Submit Error -->
      <div v-if="submitError" class="bg-red-50 rounded-xl p-4 border border-red-200">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p class="text-red-700 text-sm">{{ submitError }}</p>
        </div>
      </div>

      <!-- Selected Summary -->
      <div v-if="selectedServices.length > 0" class="bg-gray-50 rounded-xl p-4">
        <p class="text-sm font-medium text-gray-700 mb-2">
          {{ selectedServices.length }} Service(s) ausgewählt
        </p>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="serviceId in selectedServices.slice(0, 8)"
            :key="serviceId"
            class="px-2 py-0.5 text-xs bg-white border border-gray-200 rounded-full text-gray-600"
          >
            {{ getServiceName(serviceId) }}
          </span>
          <span v-if="selectedServices.length > 8" class="px-2 py-0.5 text-xs text-gray-500">
            +{{ selectedServices.length - 8 }} weitere
          </span>
        </div>
      </div>
    </div>

    <!-- Fixed Bottom Button (above bottom navigation) -->
    <div class="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-20">
      <div class="space-y-2">
        <button
          @click="saveServices"
          :disabled="isSubmitting || (!isAddingServices && hasMandatoryGroups && progressInfo.percentage < 100)"
          class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          :class="{ 'hover:bg-red-700': !isSubmitting && (isAddingServices || !hasMandatoryGroups || progressInfo.percentage === 100) }"
        >
          <span v-if="isSubmitting" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Wird gespeichert...
          </span>
          <span v-else-if="hasMandatoryGroups && progressInfo.percentage < 100">
            Pflichtfelder ausfüllen ({{ progressInfo.completed }}/{{ progressInfo.total }})
          </span>
          <span v-else>
            Weiter →
          </span>
        </button>
        <button
          v-if="!isAddingServices"
          type="button"
          @click="showCancelDialog = true"
          :disabled="isCancelling"
          class="w-full py-2 px-4 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
        >
          Check-In abbrechen
        </button>
      </div>
    </div>

    <!-- Cancel Confirmation Dialog -->
    <div v-if="showCancelDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 class="text-lg font-bold text-gray-800 mb-2">Check-In abbrechen?</h3>
        <p class="text-sm text-gray-600 mb-6">Der Check-In wird unwiderruflich gelöscht. Alle bisherigen Eingaben gehen verloren.</p>
        <div class="flex space-x-3">
          <button
            @click="showCancelDialog = false"
            class="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Zurück
          </button>
          <button
            @click="cancelCheckin(checkinIdNumber)"
            :disabled="isCancelling"
            class="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {{ isCancelling ? 'Wird gelöscht...' : 'Abbrechen' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reason prompt for removing started/completed services (audited). DEV-118 -->
    <div
      v-if="showRemoveReasonModal"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
    >
      <div class="fixed inset-0 bg-black/50" @click="showRemoveReasonModal = false"></div>
      <div class="relative w-full sm:max-w-md bg-white rounded-2xl shadow-xl p-5">
        <h3 class="text-lg font-semibold text-gray-900 mb-1">Service entfernen</h3>
        <p class="text-sm text-gray-500 mb-4">
          Mindestens ein gewählter Service wurde bereits gestartet oder abgeschlossen.
          Das Entfernen wird in der Änderungshistorie protokolliert.
        </p>
        <label class="block text-sm font-medium text-gray-700 mb-1">Grund für die Entfernung</label>
        <textarea
          v-model="removeReason"
          rows="3"
          maxlength="500"
          class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
          placeholder="Bitte einen Grund angeben (mind. 10 Zeichen)..."
        />
        <p class="text-xs mt-1" :class="removeReason.trim().length < 10 ? 'text-gray-400' : 'text-green-600'">
          {{ removeReason.trim().length }}/10 Zeichen
        </p>
        <label class="flex items-start gap-2 mt-3 cursor-pointer">
          <input
            type="checkbox"
            v-model="removeReasonConfirmed"
            class="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
          >
          <span class="text-sm text-gray-700">
            Ja, gestartete/abgeschlossene Services trotzdem entfernen
          </span>
        </label>
        <div class="flex gap-2 mt-5">
          <button
            @click="showRemoveReasonModal = false"
            class="flex-1 py-2.5 px-4 bg-white text-gray-800 rounded-lg ring-1 ring-inset ring-gray-300 hover:bg-gray-50 text-sm font-medium"
          >
            Abbrechen
          </button>
          <button
            @click="confirmRemoveWithReason"
            :disabled="removeReason.trim().length < 10 || !removeReasonConfirmed || isSubmitting"
            class="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:bg-gray-400"
          >
            {{ isSubmitting ? 'Speichere...' : 'Entfernen & speichern' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Duplicate Turnaround (DEV-220) -->
    <DuplicateTurnaroundModal
      :show="showDuplicateTurnaroundModal"
      :data="duplicateTurnaroundData"
      v-model:reason="duplicateTurnaroundReason"
      :error="duplicateTurnaroundError"
      :is-confirming="isConfirmingDuplicateTurnaround"
      :needs-reason="duplicateTurnaroundNeedsReason"
      :can-confirm="canConfirmDuplicateTurnaround"
      confirm-label="Services trotzdem hinzufügen"
      busy-label="Wird gespeichert..."
      @confirm="confirmDuplicateTurnaround"
      @close="closeDuplicateTurnaroundModal"
    />

    <!-- Duplicate Turnaround beim Anlegen der Erstauswahl (DEV-220) -->
    <DuplicateTurnaroundModal
      :show="showAttachDuplicateModal"
      :data="attachDuplicateData"
      v-model:reason="attachDuplicateReason"
      :error="attachDuplicateError"
      :is-confirming="isConfirmingAttachDuplicate"
      :needs-reason="attachDuplicateNeedsReason"
      :can-confirm="canConfirmAttachDuplicate"
      confirm-label="Turnaround trotzdem buchen"
      busy-label="Wird gespeichert..."
      @confirm="confirmAttachDuplicateTurnaround"
      @close="closeAttachDuplicateTurnaroundModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../api/axios'
import ServiceGroupCard from '../components/ServiceGroupCard.vue'
import DuplicateTurnaroundModal from '../components/DuplicateTurnaroundModal.vue'
import {
  useDuplicateTurnaround,
  isDuplicateTurnaroundAttachConflict
} from '../composables/useDuplicateTurnaround'
import { useServiceGroups, getIconForGroup, evaluateGroups, isMandatoryGroup } from '../composables/useServiceGroups'
import { useCheckinHeartbeat } from '../composables/useCheckinHeartbeat'
import { useCancelCheckin } from '../composables/useCancelCheckin'
import { useAuth } from '../composables/useAuth'

interface Service {
  id: number
  name: string
  description?: string
  requires_quantity: boolean
  isAlreadyAssigned?: boolean
  isFromPrecheckin?: boolean
  isMandatoryPrecheckin?: boolean
  precheckinReason?: string
}

const router = useRouter()
const route = useRoute()
const checkId = computed(() => route.params.id as string)
const checkinIdNumber = computed(() => parseInt(checkId.value))

// Heartbeat for connection monitoring
const {
  isConnected,
  heartbeatError,
  lockInfo,
  startHeartbeat,
  stopHeartbeat
} = useCheckinHeartbeat(checkinIdNumber)

const { isCancelling, showCancelDialog, cancelCheckin } = useCancelCheckin(stopHeartbeat)

// Service Groups integration
const {
  serviceGroups,
  recentlyUsedServices,
  validationErrors,
  turnaroundExemptServiceIds,
  custodyHandoverServiceIds,
  getExclusionConflict,
  fetchServiceGroups,
  hasMandatoryGroups,
  // fetchServiceGroups swallows failures internally and leaves an empty group
  // list — indistinguishable from "no groups configured". This ref is the only
  // failure signal; saving is blocked while it is set (fail closed, DEV-167).
  error: serviceGroupsError
} = useServiceGroups()

// UI State
const activeTab = ref(0)

// Local validation for service groups.
//
// Thin wrapper: errors come from the one derivation, like progress and validity.
const validateServiceGroupsLocally = (): boolean => {
  validationErrors.value = groupCheck.value.errors

  return validationErrors.value.length === 0
}

const isLoading = ref(true)
const isSubmitting = ref(false)
const error = ref<string | null>(null)
const submitError = ref<string | null>(null)
const availableServices = ref<Service[]>([])
const selectedServices = ref<number[]>([])
const serviceQuantities = ref<Record<number, number>>({})
const existingServiceIds = ref<number[]>([])
const isAddingServices = computed(() => route.query.add === 'true')
const existingServices = ref<any[]>([])
const canRemoveServices = ref(false)
const servicesToRemove = ref<number[]>([])
const precheckinData = ref<any>(null)
const vehicleId = ref<number | null>(null)
// Rental company of the checked-in vehicle — scopes the exclusion rules the
// service-groups endpoint returns. Null = only global rules apply. DEV-219.
const vehicleRentalCompanyId = ref<number | null>(null)

// Booked services minus the ones queued for removal — what's still "assigned"
// from the service groups' point of view (a swapped-out exclusive option must
// no longer count or lock, so the new pick can satisfy "exactly 1"). DEV-115.
const effectiveExistingServiceIds = computed(() =>
  existingServiceIds.value.filter(id => !servicesToRemove.value.includes(id))
)

// Booked services that can't be swapped out inline (completed, or started for a
// non-dispatcher) — an exclusive pick that would have to replace one of these is
// refused. Uses the strict swap predicate, NOT canRemoveService: the inline swap
// deletes without a reason, so it must stay reason-less-safe. DEV-115 / DEV-118.
const lockedExistingServiceIds = computed(() =>
  existingServices.value.filter(s => !canSwapOutService(s)).map(s => s.id)
)

// Booked services that may be removed in place (permissive canRemoveService —
// completed/started removable by dispatcher/checker). Passed to ServiceGroupCard
// so booked tiles become tappable-to-remove. DEV-119.
const removableExistingServiceIds = computed(() =>
  existingServices.value.filter(s => canRemoveService(s)).map(s => s.id)
)

// Drives the submit button's :disabled, not just the bar — same derivation as
// the save gate, so the two cannot disagree.
const progressInfo = computed(() => groupCheck.value.progress)

// Compute services that are NOT in any group
const groupedServiceIds = computed(() => {
  const ids: number[] = []
  serviceGroups.value.forEach(group => {
    group.services.forEach((s: any) => ids.push(s.id))
  })
  return ids
})

// Every service this view can render as a tap target: the grouped ones plus the
// flat / "Weitere Services" list. On most instances only a handful of services
// are grouped, so the ungrouped ones must be covered too. DEV-219.
const selectableServiceIds = computed(() => {
  const ids = new Set<number>(groupedServiceIds.value)
  availableServices.value.forEach(s => ids.add(s.id))
  return [...ids]
})

// Services currently blocked by an exclusion rule (DEV-219): service id →
// rule message. A service already active (selected or booked) is never blocked;
// deselecting the trigger service frees its partners again (pure computed).
const exclusionConflicts = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {}
  // Same combined selection the group check uses — TRK-15 collapsed the second,
  // independently computed copy of it: two expressions of one selection in one
  // file is exactly the shape that let the exemption flag and the validation
  // drift apart in the first place.
  const active = groupCheck.value.selection
  for (const id of selectableServiceIds.value) {
    if (active.includes(id)) continue
    const conflict = getExclusionConflict(id, active)
    if (conflict) map[id] = conflict.message
  }
  return map
})

const displayableServices = computed(() => {
  if (serviceGroups.value.length === 0) {
    return availableServices.value
  }
  return availableServices.value.filter(s => !groupedServiceIds.value.includes(s.id))
})

// Helper methods
const isGroupMandatory = (group: any) => isMandatoryGroup(group)

const isGroupValid = (group: any) => groupCheck.value.validity[group.id] ?? true

const getServiceName = (serviceId: number): string => {
  // Check in service groups first
  for (const group of serviceGroups.value) {
    const service = group.services.find((s: any) => s.id === serviceId)
    if (service) return service.name
  }
  // Check in available services
  const service = availableServices.value.find(s => s.id === serviceId)
  return service?.name || `Service ${serviceId}`
}

const getServiceClasses = (service: Service) => {
  if (servicesToRemove.value.includes(service.id)) {
    return 'border-red-400 bg-red-50 cursor-pointer'
  }
  if (service.isAlreadyAssigned) {
    // Booked: tappable to remove if allowed for this role, else inert. DEV-119.
    return canRemoveExistingById(service.id)
      ? 'border-green-300 bg-green-50 cursor-pointer'
      : 'border-green-300 bg-green-50 cursor-not-allowed opacity-60'
  }
  if (selectedServices.value.includes(service.id)) {
    return 'border-[#E9041D] bg-red-50'
  }
  if (isServiceExcluded(service.id)) {
    // Excluded by a rule: greyed like a locked booked tile, same as in the
    // grouped view (ServiceGroupCard). DEV-219.
    return 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
  }
  return 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
}

// Blocked by an exclusion rule against the active set (DEV-219) — used by the
// flat / "Weitere Services" tiles, which don't go through ServiceGroupCard.
const isServiceExcluded = (serviceId: number): boolean => {
  return exclusionConflicts.value[serviceId] !== undefined
}

// Indicator (checkbox dot) color for the flat/"Weitere" lists. DEV-119.
const getServiceIndicatorClasses = (service: any): string => {
  if (servicesToRemove.value.includes(service.id)) return 'border-red-500 bg-red-500'
  if (service.isAlreadyAssigned) return 'border-green-500 bg-green-500'
  if (selectedServices.value.includes(service.id)) return 'border-[#E9041D] bg-[#E9041D]'
  return 'border-gray-300'
}

// Whether a booked service (by id) may be removed by the current role. DEV-119.
const canRemoveExistingById = (id: number): boolean => {
  const s = existingServices.value.find(e => e.id === id)
  return s ? canRemoveService(s) : false
}

// Tap on a flat/"Weitere" list tile: booked → toggle removal; otherwise add. DEV-119.
const onAvailableTileClick = (service: any) => {
  if (service.isAlreadyAssigned) {
    handleToggleRemoveExisting(service.id)
  } else if (isServiceExcluded(service.id)) {
    // Excluded tile: refuse the pick and surface why, like ServiceGroupCard. DEV-219.
    handleExclusionBlocked(service.id, exclusionConflicts.value[service.id])
  } else {
    toggleService(service)
  }
}

const toggleRecentService = (serviceId: number) => {
  const index = selectedServices.value.indexOf(serviceId)
  if (index === -1) {
    // DEV-219: the shortcut must respect exclusions like every other picker.
    const conflict = exclusionConflicts.value[serviceId]
    if (conflict) {
      handleExclusionBlocked(serviceId, conflict)
      return
    }
    selectedServices.value.push(serviceId)
  } else {
    selectedServices.value.splice(index, 1)
  }
}

const handleGroupSelection = (newSelection: number[]) => {
  selectedServices.value = newSelection
}

// Exclusive-choice swap (DEV-115): ServiceGroupCard asks to remove the already-
// booked sibling(s) when the user picks the other option. Queue removable ones
// (the save will remove + add together); if a booked one is completed it can't
// be replaced, so surface that and don't queue it.
const handleRequestRemoveExisting = (serviceIds: number[]) => {
  for (const id of serviceIds) {
    if (servicesToRemove.value.includes(id)) continue
    const service = existingServices.value.find(s => s.id === id)
    if (service && canSwapOutService(service)) {
      servicesToRemove.value.push(id)
    } else if (service) {
      submitError.value = `„${service.name}" ist bereits abgeschlossen und kann nicht ersetzt werden.`
    }
  }
}

// ServiceGroupCard refused an exclusive pick because the booked sibling can't be
// replaced (completed / not removable). Explain why; the pick was not applied.
const handleSwapBlocked = (serviceId: number) => {
  const service = existingServices.value.find(s => s.id === serviceId)
  submitError.value = service
    ? `„${service.name}" ist bereits abgeschlossen und kann nicht ersetzt werden.`
    : 'Dieser Service kann nicht ersetzt werden.'
}

// A tile blocked by an exclusion rule was tapped — surface the rule message
// (same channel as swapBlocked); the pick was not applied. DEV-219.
const handleExclusionBlocked = (_serviceId: number, message: string) => {
  submitError.value = message
}

// A booked service tile (in a group or in "Weitere") was tapped to toggle it
// in/out of the removal queue — reuses the same guard + queue as the explicit
// removal flow. The reason prompt fires later, at Save. DEV-119.
const handleToggleRemoveExisting = (serviceId: number) => {
  const service = existingServices.value.find(s => s.id === serviceId)
  if (service) toggleServiceRemoval(service)
}

const toggleService = (service: Service) => {
  if (service.isAlreadyAssigned) return

  const index = selectedServices.value.indexOf(service.id)
  if (index === -1) {
    selectedServices.value.push(service.id)
    if (service.requires_quantity) {
      serviceQuantities.value[service.id] = 1
    }
  } else {
    selectedServices.value.splice(index, 1)
    delete serviceQuantities.value[service.id]
  }
}

// Service removal helpers
const { isDisponent, isChecker, isSchichtleiter, isAdmin } = useAuth()

// DEV-240, in der Fassung nach TRK-15: eine ABHOLUNG ("FZG Abholung
// Service-Center", aufgelöst über die Einstellung `pickup_service`) ERSETZT den
// Turnaround — eine Fremdfirma nimmt das Fahrzeug, bei uns fällt keine Leistung
// an. Disposition/Schichtleitung/Admin dürfen dann ohne die Pflichtgruppe
// anlegen; Checker behalten die Pflicht.
//
// DEV-240 formulierte das ursprünglich als „ein Service, der die Obhut beendet".
// Das war der Fehler: dieses Flag trägt auf der Produktion die Überführung
// Terminal 3, wo ein Turnaround sehr wohl dazugehört (Termin 12.10.).
//
// TRK-15: das Ergebnis geht IN die Regel, statt die
// Prüfung als Ganzes zu überspringen — sonst fällt mit der Pflicht auch die
// Exklusivitäts-Hälfte aus.
//
// ONE source for both halves of the group check — the validated selection and
// the turnaround exemption derived from it. Keeping them together is
// load-bearing: as two independent expressions they disagreed, and a Disponent
// re-opening a check-in with an already-booked pickup was told to add the
// Turnaround that the billing review corrects in 174 of 179 cases.
//
// NOT part of this derivation: whether the selection ends our custody. That is
// keyWillBeHandedOver below, on a different list — see the note there.
//
// The derivation lives in evaluateGroups (useServiceGroups.ts) and is
// unit-tested there. What is left here is wiring.
//
// The exempting ids come from the server (`turnaround_exempt_service_ids`), not
// from the local service catalogue. That removed an older false negative: while
// the flag was read from `availableServices`, a BOOKED pickup outside that
// pre-filtered set yielded no exemption at all. `is_locked` is filtered server
// side, in exemptingServiceIds(), before the ids are published.
//
// Not covered by any test: that this view wires groupCheck up at all — every
// consumer of it below, and the template bindings that carry its results (the
// card's :turnaround-replaced, the DEV-240 banner's v-if, the tab ticks). This
// view has no component test, so none of that wiring is observed. Stated as a
// property rather than a list on purpose: five earlier attempts to enumerate it
// were each incomplete.
//
// There are TWO save gates: the handler condition below and the submit button's
// :disabled. Both read this one unit-tested derivation, so they agree on which
// groups are satisfied — though the button compares a ROUNDED percentage, which
// only diverges from "all satisfied" above 200 mandatory groups (production has
// one per site), and in the harmless direction: the handler then refuses with a
// message instead of the button locking.
//
// Covered by the Phase-5 smoke: re-enter a check-in with a booked pickup, as
// Disponent and as Checker.
const groupCheck = computed(() =>
  evaluateGroups(
    serviceGroups.value,
    effectiveExistingServiceIds.value,
    selectedServices.value,
    turnaroundExemptServiceIds.value,
    // DEV-240 limits the exemption to Disposition/Schichtleitung/Admin;
    // checkers keep the obligation. Kept as decided in TRK-15.
    isDisponent.value || isSchichtleiter.value || isAdmin.value
  )
)

const turnaroundIsReplaced = computed(() => groupCheck.value.turnaroundReplaced)

// Separate Frage: beendet die Auswahl unsere Obhut? Das steuert die automatische
// Schlüsselübergabe (KeyStatusService) und hängt an einer anderen Liste.
const keyWillBeHandedOver = computed(() =>
  groupCheck.value.selection.some(id => custodyHandoverServiceIds.value.includes(id))
)

// dispatcher + checker may remove started/completed services (with a reason that
// is audited to the key-editor Änderungshistorie). DEV-118.
const canManageStartedServices = computed(() => isDisponent.value || isChecker.value)

const isServiceStarted = (service: any): boolean => {
  // A service is started if started_at is set (API returns started_at directly, not in pivot)
  return service.pivot?.started_at != null || service.started_at != null
}

const isServiceCompleted = (service: any): boolean => {
  // A service is completed if status is 'completed' OR both started_at and completed_at/ended_at are set
  // Note: API returns 'completed_at' not 'ended_at'
  if (service.status === 'completed') return true
  const started = service.pivot?.started_at != null || service.started_at != null
  const ended = service.pivot?.ended_at != null || service.ended_at != null || service.completed_at != null
  return started && ended
}

// Explicit removal (the "Bereits zugewiesene Services" tags): dispatcher/checker
// may remove started/completed services; everyone may remove not-started ones.
// Removing a started/completed one triggers the mandatory reason prompt. DEV-118.
const canRemoveService = (service: any): boolean => {
  if (isServiceCompleted(service) || isServiceStarted(service)) return canManageStartedServices.value
  return true
}

// Inline exclusive-choice swap (DEV-115) stays strict and reason-less: a completed
// option is never swappable inline, and started ones only by a dispatcher. The
// audited reason flow lives only in the explicit removal path above, so the swap
// (which deletes without a reason) must not queue started/completed services.
const canSwapOutService = (service: any): boolean => {
  if (isServiceCompleted(service)) return false
  if (isServiceStarted(service)) return isDisponent.value
  return true
}

const toggleServiceRemoval = (service: any) => {
  // Check if service can be removed
  if (!canRemoveService(service)) return

  const index = servicesToRemove.value.indexOf(service.id)
  if (index === -1) {
    servicesToRemove.value.push(service.id)
  } else {
    servicesToRemove.value.splice(index, 1)
  }
}

// Read-only status colors for the "Bereits zugewiesene Services" overview box.
// Removal happens in the selection area below, not here. DEV-119.
const getExistingSummaryClasses = (service: any): string => {
  if (servicesToRemove.value.includes(service.id)) {
    return 'bg-red-100 text-red-700 border border-red-300'
  }
  if (isServiceCompleted(service)) {
    return 'bg-green-100 text-green-700 border border-green-200'
  }
  if (isServiceStarted(service)) {
    return 'bg-orange-100 text-orange-700 border border-orange-200'
  }
  return 'bg-white text-gray-600 border border-gray-200'
}

// State for the mandatory-reason prompt shown before removing started/completed
// services (audited to the key-editor Änderungshistorie). DEV-118.
const showRemoveReasonModal = ref(false)
const removeReason = ref('')
const removeReasonConfirmed = ref(false)

// True when the current removal selection includes a started/completed service.
const removalNeedsReason = computed(() =>
  existingServices.value.some(
    s => servicesToRemove.value.includes(s.id) && (isServiceStarted(s) || isServiceCompleted(s))
  )
)

// A reason captured for the current Save when the removal queue contains a
// started/completed service. Reset after each Save. DEV-119.
const pendingRemovalReason = ref<string | null>(null)

// Modal confirm → store the reason and resume the Save it interrupted.
const confirmRemoveWithReason = () => {
  if (removeReason.value.trim().length < 10 || !removeReasonConfirmed.value) return
  pendingRemovalReason.value = removeReason.value.trim()
  showRemoveReasonModal.value = false
  saveServices()
}

// Fetch services
const fetchServices = async () => {
  isLoading.value = true
  error.value = null

  try {
    // Get checkin details (vehicle ID + existing services)
    let checkinDetail: any = null
    try {
      const checkinResponse = await api.get(`/api/checker/checkins/${checkId.value}`)
      checkinDetail = checkinResponse.data?.data
      if (checkinDetail?.vehicle?.id) {
        vehicleId.value = checkinDetail.vehicle.id
        vehicleRentalCompanyId.value = checkinDetail.vehicle.rental_company?.id
          ?? checkinDetail.vehicle.rental_company_id
          ?? null
      }
    } catch (err) {
      console.log('Could not fetch checkin details yet')
    }

    // Load already-booked services in BOTH modes (DEV-167): a checker re-entering
    // the initial selection must see what is booked, or a "correction" silently
    // double-books. VehicleCheckinResource returns services either as a flat array
    // OR (when the relation is loaded) as a summary object { total, completed,
    // pending, list: [...] } whose items are flat (status/started_at/completed_at,
    // no pivot). Normalize both.
    if (checkinDetail) {
      try {
        const bookedServices: any[] = Array.isArray(checkinDetail?.services)
          ? checkinDetail.services
          : (checkinDetail?.services?.list ?? [])

        if (bookedServices.length > 0) {
          existingServiceIds.value = bookedServices.map((s: any) => s.id)
          existingServices.value = bookedServices.map((s: any) => {
            const startedAt = s.started_at ?? s.pivot?.started_at ?? null
            const endedAt = s.completed_at ?? s.pivot?.ended_at ?? null
            return {
              id: s.id,
              name: s.name,
              status: s.status ?? (endedAt ? 'completed' : 'pending'),
              completed_at: endedAt,
              started_at: startedAt,
              pivot: s.pivot ?? { started_at: startedAt, ended_at: endedAt },
              // A service can still be removed only if it hasn't been started.
              can_remove: !startedAt
            }
          })
          canRemoveServices.value = true
        }
      } catch (err) {
        console.log('Could not fetch checkin services')
      }
    }

    // Get the selected location to filter services by location type
    const { getSelectedLocation } = useAuth()
    const selectedLocation = getSelectedLocation()
    const locationType = selectedLocation?.type || null

    const response = await api.get('/api/services', {
      params: locationType ? { location_type: locationType } : {}
    })
    let services = response.data.data

    services = services.map((service: Service) => ({
      ...service,
      isAlreadyAssigned: existingServiceIds.value.includes(service.id)
    }))

    availableServices.value = services

    // Fetch service groups
    try {
      await fetchServiceGroups(vehicleRentalCompanyId.value)
      console.log('Service groups loaded:', serviceGroups.value.length, 'groups')
    } catch (err) {
      console.log('Could not load service groups')
    }

    // Fetch pre-checkin data
    if (vehicleId.value) {
      try {
        const precheckinResponse = await api.get(`/api/checker/checkins/vehicle/${vehicleId.value}/precheckin`)
        precheckinData.value = precheckinResponse.data?.data

        if (precheckinData.value?.services) {
          precheckinData.value.services.forEach((precheckinService: any) => {
            const serviceIndex = availableServices.value.findIndex(s => s.id === precheckinService.id)
            if (serviceIndex !== -1) {
              availableServices.value[serviceIndex].isFromPrecheckin = true
              availableServices.value[serviceIndex].isMandatoryPrecheckin = precheckinService.is_mandatory
              availableServices.value[serviceIndex].precheckinReason = precheckinService.reason

              if (!selectedServices.value.includes(precheckinService.id) && !existingServiceIds.value.includes(precheckinService.id)) {
                selectedServices.value.push(precheckinService.id)
              }
            }
          })
        }
      } catch (err) {
        console.log('No pre-checkin found')
      }
    }

    // Initialize quantities
    availableServices.value.forEach(service => {
      if (service.requires_quantity && !service.isAlreadyAssigned) {
        serviceQuantities.value[service.id] = 1
      }
    })
  } catch (err: any) {
    console.error('Fehler beim Laden der Services:', err)
    error.value = 'Die Services konnten nicht geladen werden.'
  } finally {
    isLoading.value = false
  }
}

// DEV-220 --------------------------------------------------------------
// This is the endpoint the live PWA flow actually books a turnaround on, so the
// backend duplicate guard fires here (409 → confirm, 422 → hard block).
// True once the current Save has already flushed its queued removals to the
// server. The duplicate guard can only reject the *add* — the removals are then
// already committed, so the retry must not re-send them and the user has to be
// told what was saved. DEV-220.
const removalsFlushedThisSave = ref(false)

// Issues the add-services call — and only that call, so it doubles as the
// confirmation retry. Success handling (navigation) lives in here.
const submitAddServices = async (payload: Record<string, any>) => {
  await api.post(`/api/vehicle-checkins/${checkId.value}/add-services`, payload)
  router.push('/dashboard')
}

const {
  showModal: showDuplicateTurnaroundModal,
  data: duplicateTurnaroundData,
  reason: duplicateTurnaroundReason,
  modalError: duplicateTurnaroundError,
  isConfirming: isConfirmingDuplicateTurnaround,
  needsReason: duplicateTurnaroundNeedsReason,
  canConfirm: canConfirmDuplicateTurnaround,
  submitGuarded: submitAddServicesGuarded,
  confirm: confirmDuplicateTurnaround,
  close: closeDuplicateTurnaround
} = useDuplicateTurnaround<Record<string, any>>({
  submit: payload => submitAddServices(payload),
  onBlocked: message => {
    // Hard block (identischer Kilometerstand) — kein Retry, normaler Fehlerkanal.
    submitError.value = message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  },
  confirmErrorMessage: 'Services konnten nicht hinzugefügt werden. Bitte die Disposition kontaktieren.'
})

// Cancel in the modal: nothing was added, but queued removals of this Save are
// already committed. Name that explicitly instead of leaving a silent half state.
const closeDuplicateTurnaroundModal = () => {
  closeDuplicateTurnaround()
  submitError.value = removalsFlushedThisSave.value
    ? 'Entfernte Services wurden gespeichert, die neuen Services wurden NICHT hinzugefügt.'
    : 'Die Services wurden nicht hinzugefügt.'
  removalsFlushedThisSave.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// DEV-220 — initial service selection ----------------------------------
// The per-service attach POST /api/vehicle-checkin-services answers the guard
// with 422 + error: 'duplicate_turnaround' (not 409), because the attach loop
// historically skips every 409 silently. Own composable instance: the payload is
// a single service, and the loop must resume with the remaining services after a
// confirmation.
const attachDuplicateHardBlocked = ref(false)

// Issues one attach — and only that one, so it doubles as the confirmation retry.
const submitAttachService = async (payload: Record<string, any>) => {
  await api.post('/api/vehicle-checkin-services', payload)
}

const {
  showModal: showAttachDuplicateModal,
  data: attachDuplicateData,
  reason: attachDuplicateReason,
  modalError: attachDuplicateError,
  isConfirming: isConfirmingAttachDuplicate,
  needsReason: attachDuplicateNeedsReason,
  canConfirm: canConfirmAttachDuplicate,
  submitGuarded: attachServiceGuarded,
  confirm: confirmAttachDuplicate,
  close: closeAttachDuplicate
} = useDuplicateTurnaround<Record<string, any>>({
  submit: payload => submitAttachService(payload),
  isConflict: isDuplicateTurnaroundAttachConflict,
  onBlocked: message => {
    // Hard block (identischer Kilometerstand) — kein Retry, Schleife bricht ab.
    attachDuplicateHardBlocked.value = true
    submitError.value = message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  },
  confirmErrorMessage: 'Der Service konnte nicht gespeichert werden. Bitte die Disposition kontaktieren.'
})

// The attach loop parks on this deferred while the modal is open: the modal is
// event driven, so confirm/cancel resolve the promise the loop is awaiting.
let attachDuplicateDecision: ((value: 'confirmed' | 'cancelled') => void) | null = null

const waitForAttachDuplicateDecision = () =>
  new Promise<'confirmed' | 'cancelled'>(resolve => {
    attachDuplicateDecision = resolve
  })

const resolveAttachDuplicateDecision = (value: 'confirmed' | 'cancelled') => {
  const resolve = attachDuplicateDecision
  attachDuplicateDecision = null
  resolve?.(value)
}

const confirmAttachDuplicateTurnaround = async () => {
  await confirmAttachDuplicate()
  // The composable closes the modal only when the retry succeeded; on a failure
  // it stays open with an error, so the loop stays parked and the user may
  // retry or cancel.
  if (!showAttachDuplicateModal.value) {
    resolveAttachDuplicateDecision('confirmed')
  }
}

const closeAttachDuplicateTurnaroundModal = () => {
  closeAttachDuplicate()
  resolveAttachDuplicateDecision('cancelled')
}

// Save services
const saveServices = async () => {
  isSubmitting.value = true
  submitError.value = null
  removalsFlushedThisSave.value = false

  // Fail closed (DEV-167): without the group list, exclusivity cannot be
  // checked client-side — an empty list caused by a fetch failure must not
  // degrade into an unvalidated flat save. One retry, then block; the DEV-166
  // server guard remains the backstop.
  if (serviceGroupsError.value) {
    await fetchServiceGroups(vehicleRentalCompanyId.value)
    if (serviceGroupsError.value) {
      submitError.value = 'Service-Gruppen konnten nicht geladen werden. Speichern ist gesperrt. Bitte Verbindung prüfen und erneut versuchen.'
      isSubmitting.value = false
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
  }

  // Skip validation for add mode - user may just want to go back after removing services.
  //
  // TRK-15: the DEV-240 turnaround exemption no longer skips this block. It is an
  // input to the rule now (turnaroundIsReplaced → groupCheck), so a
  // pickup check-in passes without a Turnaround while the exclusive "at most one"
  // half keeps applying — skipping wholesale switched that half off too.
  if (!isAddingServices.value && serviceGroups.value.length > 0) {
    const isValid = validateServiceGroupsLocally()
    if (!isValid) {
      submitError.value = 'Bitte alle Pflichtfelder ausfüllen.'
      isSubmitting.value = false
      // Find first invalid group and switch to it
      for (let i = 0; i < serviceGroups.value.length; i++) {
        if (!isGroupValid(serviceGroups.value[i]) && isGroupMandatory(serviceGroups.value[i])) {
          activeTab.value = i
          break
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
  }

  const servicesToAdd = selectedServices.value.filter(id => !existingServiceIds.value.includes(id))

  // Require a selection only on a truly empty first pass — a re-entered checkin
  // with booked services or queued removals may proceed unchanged (DEV-167).
  if (!isAddingServices.value && servicesToAdd.length === 0 && selectedServices.value.length === 0
    && servicesToRemove.value.length === 0 && existingServiceIds.value.length === 0) {
    submitError.value = 'Bitte wähle mindestens einen Service aus.'
    isSubmitting.value = false
    return
  }

  // If the removal queue includes a started/completed service, a reason is
  // mandatory (audited to the key-editor history) — in both modes (DEV-167).
  // Prompt for it, then the modal resumes this Save with pendingRemovalReason
  // set. DEV-118/119.
  if (servicesToRemove.value.length > 0 && removalNeedsReason.value && !pendingRemovalReason.value) {
    removeReason.value = ''
    removeReasonConfirmed.value = false
    showRemoveReasonModal.value = true
    isSubmitting.value = false
    return
  }

  if (isAddingServices.value && (!checkId.value || checkId.value === 'undefined')) {
    submitError.value = 'Check-In ID fehlt.'
    isSubmitting.value = false
    return
  }

  try {
    const finalServicesToAdd = servicesToAdd.length > 0
      ? servicesToAdd
      : selectedServices.value.filter(id => !existingServiceIds.value.includes(id))

    // Process queued removals first — in BOTH modes (DEV-167): the exclusive-
    // choice swap on a re-entered initial selection must remove the old choice
    // before the sibling is attached, or the DEV-166 server guard rejects the
    // add. A reason is attached when a started/completed service is in the
    // queue. DEV-115/118/119.
    if (servicesToRemove.value.length > 0) {
      await api.delete(`/api/vehicle-checkins/${checkId.value}/remove-services`, {
        data: {
          service_ids: servicesToRemove.value,
          ...(pendingRemovalReason.value ? { reason: pendingRemovalReason.value } : {})
        }
      })
      existingServiceIds.value = existingServiceIds.value.filter(id => !servicesToRemove.value.includes(id))
      existingServices.value = existingServices.value.filter(s => !servicesToRemove.value.includes(s.id))
      servicesToRemove.value = []
      pendingRemovalReason.value = null
      // Committed server-side AND mirrored locally above — a following duplicate
      // prompt therefore retries the add alone, never these removals. DEV-220.
      removalsFlushedThisSave.value = true
    }

    if (isAddingServices.value) {
      // If no new services to add, just go back to dashboard
      if (finalServicesToAdd.length === 0) {
        router.push('/dashboard')
        return
      }

      // DEV-220: a duplicate turnaround opens the confirmation modal (or reports
      // the hard block) and returns false; the retry re-sends this call only.
      await submitAddServicesGuarded({ service_ids: finalServicesToAdd })
    } else {
      let alreadyAttachedServices: number[] = []
      try {
        const checkinResponse = await api.get(`/api/checker/checkins/${checkId.value}`)
        if (checkinResponse.data?.data?.services) {
          alreadyAttachedServices = checkinResponse.data.data.services.map((s: any) => s.id)
        }
      } catch (err) {
        console.log('Could not fetch current services')
      }

      const failedServices: number[] = []
      // Attached during THIS save — named back to the user if the run is aborted
      // by a cancelled duplicate-turnaround prompt. DEV-220.
      const attachedServices: number[] = []
      attachDuplicateHardBlocked.value = false

      for (const serviceId of selectedServices.value) {
        if (!alreadyAttachedServices.includes(serviceId)) {
          try {
            // DEV-220: returns false for a duplicate turnaround (modal opened, or
            // hard block reported); every other error is rethrown and handled below.
            const saved = await attachServiceGuarded({
              vehicle_checkin_id: checkId.value,
              service_id: serviceId
            })

            if (!saved) {
              if (attachDuplicateHardBlocked.value) {
                // Nicht überschreibbar — Meldung steht schon in submitError.
                attachDuplicateHardBlocked.value = false
                await fetchServices()
                selectedServices.value = selectedServices.value.filter(
                  id => !existingServiceIds.value.includes(id)
                )
                return
              }

              // Modal is open — park here until the user confirms or cancels.
              const decision = await waitForAttachDuplicateDecision()

              if (decision === 'cancelled') {
                const savedNames = attachedServices.map(getServiceName).join(', ')
                submitError.value = attachedServices.length > 0
                  ? `Gespeichert wurden: ${savedNames}. Der Turnaround „${getServiceName(serviceId)}" wurde NICHT hinzugefügt, die restlichen Services wurden nicht gespeichert.`
                  : `Der Turnaround „${getServiceName(serviceId)}" wurde NICHT hinzugefügt, es wurden keine Services gespeichert.`
                await fetchServices()
                selectedServices.value = selectedServices.value.filter(
                  id => !existingServiceIds.value.includes(id)
                )
                window.scrollTo({ top: 0, behavior: 'smooth' })
                return
              }
            }

            // Saved right away or after the confirmation — continue with the rest.
            attachedServices.push(serviceId)
          } catch (err: any) {
            // Skip duplicates (already attached) — continue with remaining services
            if (err.response?.status === 409) {
              continue
            }
            // 422 = the DEV-166 server guard refused the attach (exclusive_choice
            // conflict). Surface the server's message, re-sync the booked state,
            // and stay on this view instead of navigating to complete (DEV-167).
            if (err.response?.status === 422) {
              submitError.value = err.response.data?.message
                ?? 'Service konnte nicht gespeichert werden (Gruppenkonflikt).'
              await fetchServices()
              selectedServices.value = selectedServices.value.filter(
                id => !existingServiceIds.value.includes(id)
              )
              window.scrollTo({ top: 0, behavior: 'smooth' })
              return
            }
            console.error(`Failed to attach service ${serviceId}:`, err)
            failedServices.push(serviceId)
          }
        }
      }

      if (failedServices.length > 0) {
        throw new Error(`${failedServices.length} Service(s) konnten nicht gespeichert werden.`)
      }

      router.push(`/checks/${checkId.value}/complete`)
    }
  } catch (err: any) {
    console.error('Fehler beim Speichern:', err)
    const errorMessage = err instanceof Error ? err.message : 'Speichern fehlgeschlagen.'
    submitError.value = errorMessage
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  fetchServices()
  // Start heartbeat to keep checkin alive
  if (checkinIdNumber.value) {
    startHeartbeat()
  }
})

onUnmounted(() => {
  stopHeartbeat()
})
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
